import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// Controller for drag-and-drop reordering of content blocks.
// Sends updated positions to the server via PATCH request.
//
// Usage:
//   <div data-controller="content-blocks-sortable"
//        data-content-blocks-sortable-url-value="/panel/articles/123/content_blocks/reorder">
//     <div data-content-blocks-sortable-target="item" data-block-id="1">
//       <div data-content-blocks-sortable-target="handle">...</div>
//     </div>
//   </div>
export default class extends Controller {
  static targets = ["item", "handle"]
  static values = {
    url: String  // The reorder endpoint URL
  }

  connect() {
    this.sortable = Sortable.create(this.element, {
      handle: "[data-content-blocks-sortable-target='handle']",
      animation: 150,
      ghostClass: "opacity-50",
      chosenClass: "bg-indigo-50",
      dragClass: "shadow-lg",
      onEnd: this.handleDragEnd.bind(this)
    })
  }

  disconnect() {
    if (this.sortable) {
      this.sortable.destroy()
    }
  }

  handleDragEnd(event) {
    // Only process if position actually changed
    if (event.oldIndex === event.newIndex) {
      return
    }

    // Build positions object from current order
    const positions = {}
    this.itemTargets.forEach((item, index) => {
      const blockId = item.dataset.blockId
      if (blockId) {
        positions[blockId] = index
      }
    })

    // Send update to server
    this.updatePositions(positions)
  }

  async updatePositions(positions) {
    try {
      const response = await fetch(this.urlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({ positions })
      })

      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`)
      }

      this.showFeedback("Block order saved", "success")
      this.revealPublishBanner()
    } catch (error) {
      console.error("Failed to save block order:", error)
      this.showFeedback("Failed to save order", "error")

      // Optionally reload to restore server state
      // window.location.reload()
    }
  }

  get csrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']")
    return meta ? meta.content : ""
  }

  revealPublishBanner() {
    const banner = document.getElementById("unpublished-changes-banner")
    if (banner) banner.removeAttribute("hidden")
  }

  showFeedback(message, type) {
    // Create a temporary toast notification
    const toast = document.createElement("div")
    toast.className = `fixed bottom-4 right-4 px-4 py-2 rounded-lg shadow-lg z-50 transition-all transform translate-y-0 opacity-100 ${
      type === "success" ? "bg-green-500 text-white" : "bg-red-500 text-white"
    }`
    toast.textContent = message

    document.body.appendChild(toast)

    // Fade out and remove
    setTimeout(() => {
      toast.style.opacity = "0"
      toast.style.transform = "translateY(10px)"
      setTimeout(() => toast.remove(), 300)
    }, 2000)
  }
}
